import { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Edit2, Trash2, DollarSign, Users, TrendingUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function InfluencerCommissions({ embedded = false }) {
  const [influencers, setInfluencers] = useState([]);
  const [sales, setSales] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saleDialogOpen, setSaleDialogOpen] = useState(false);
  const [editingInfluencer, setEditingInfluencer] = useState(null);
  const [selectedInfluencer, setSelectedInfluencer] = useState(null);

  const [influencerForm, setInfluencerForm] = useState({
    name: '',
    social_handle: '',
    email: '',
    commission_rate: 15,
    notes: '',
  });

  const [saleForm, setSaleForm] = useState({
    influencer_id: '',
    event_id: '',
    quantity: 1,
    sale_amount: '',
    notes: '',
    date: new Date().toISOString().split('T')[0],
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('vyrus_token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [influencersRes, salesRes, eventsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/influencers`, { headers }),
        axios.get(`${BACKEND_URL}/api/influencer-sales`, { headers }),
        axios.get(`${BACKEND_URL}/api/events`, { headers }),
      ]);
      
      setInfluencers(influencersRes.data);
      setSales(salesRes.data);
      setEvents(eventsRes.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleInfluencerSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('vyrus_token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const payload = {
        ...influencerForm,
        commission_rate: parseFloat(influencerForm.commission_rate) / 100,
      };

      if (editingInfluencer) {
        await axios.put(`${BACKEND_URL}/api/influencers/${editingInfluencer.id}`, payload, { headers });
        toast.success('Influencer updated');
      } else {
        await axios.post(`${BACKEND_URL}/api/influencers`, payload, { headers });
        toast.success('Influencer added');
      }
      
      setDialogOpen(false);
      setEditingInfluencer(null);
      resetInfluencerForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save influencer');
    }
  };

  const handleSaleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('vyrus_token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const payload = {
        ...saleForm,
        quantity: parseInt(saleForm.quantity),
        sale_amount: parseFloat(saleForm.sale_amount),
        event_id: saleForm.event_id || null,
      };

      await axios.post(`${BACKEND_URL}/api/influencer-sales`, payload, { headers });
      toast.success('Sale recorded');
      
      setSaleDialogOpen(false);
      resetSaleForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record sale');
    }
  };

  const handleDeleteInfluencer = async (id) => {
    if (!window.confirm('Delete this influencer and all their sales records?')) return;
    try {
      const token = localStorage.getItem('vyrus_token');
      await axios.delete(`${BACKEND_URL}/api/influencers/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Influencer deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handleDeleteSale = async (id) => {
    if (!window.confirm('Delete this sale record?')) return;
    try {
      const token = localStorage.getItem('vyrus_token');
      await axios.delete(`${BACKEND_URL}/api/influencer-sales/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Sale deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const openEditInfluencer = (inf) => {
    setEditingInfluencer(inf);
    setInfluencerForm({
      name: inf.name,
      social_handle: inf.social_handle || '',
      email: inf.email || '',
      commission_rate: (inf.commission_rate * 100).toFixed(0),
      notes: inf.notes || '',
    });
    setDialogOpen(true);
  };

  const openAddSale = (inf) => {
    setSelectedInfluencer(inf);
    setSaleForm({
      ...saleForm,
      influencer_id: inf.id,
    });
    setSaleDialogOpen(true);
  };

  const resetInfluencerForm = () => {
    setInfluencerForm({
      name: '',
      social_handle: '',
      email: '',
      commission_rate: 15,
      notes: '',
    });
  };

  const resetSaleForm = () => {
    setSaleForm({
      influencer_id: '',
      event_id: '',
      quantity: 1,
      sale_amount: '',
      notes: '',
      date: new Date().toISOString().split('T')[0],
    });
    setSelectedInfluencer(null);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  const totalOwed = influencers.reduce((sum, inf) => sum + (inf.total_owed || 0), 0);
  const totalSales = influencers.reduce((sum, inf) => sum + (inf.total_sales || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {!embedded && (
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-4xl font-bold font-heading tracking-tight" data-testid="influencer-title">
              Cosplayer & Influencer Commissions
            </h1>
            <p className="text-muted-foreground mt-1">Track trading card sales and commissions owed</p>
          </div>
          <Button onClick={() => { resetInfluencerForm(); setEditingInfluencer(null); setDialogOpen(true); }} data-testid="add-influencer-btn">
            <Plus className="h-4 w-4 mr-2" />
            Add Influencer
          </Button>
        </div>
      )}
      {embedded && (
        <div className="flex justify-end">
          <Button onClick={() => { resetInfluencerForm(); setEditingInfluencer(null); setDialogOpen(true); }} data-testid="add-influencer-btn">
            <Plus className="h-4 w-4 mr-2" />
            Add Influencer
          </Button>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-card border-border/50">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-primary/10">
                <Users className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Influencers</p>
                <p className="text-2xl font-bold">{influencers.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border/50">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-green-500/10">
                <TrendingUp className="h-6 w-6 text-green-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Sales</p>
                <p className="text-2xl font-bold text-green-400">{formatCurrency(totalSales)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border/50">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-red-500/10">
                <DollarSign className="h-6 w-6 text-red-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Owed</p>
                <p className="text-2xl font-bold text-red-400">{formatCurrency(totalOwed)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="influencers" className="w-full">
        <TabsList>
          <TabsTrigger value="influencers">Influencers</TabsTrigger>
          <TabsTrigger value="sales">All Sales</TabsTrigger>
        </TabsList>

        <TabsContent value="influencers" className="space-y-4 mt-6">
          {influencers.length === 0 ? (
            <Card className="p-12 text-center bg-card border-border/50">
              <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground mb-4">No influencers added yet</p>
              <Button onClick={() => { resetInfluencerForm(); setDialogOpen(true); }}>
                <Plus className="h-4 w-4 mr-2" />
                Add Your First Influencer
              </Button>
            </Card>
          ) : (
            <div className="grid gap-4">
              {influencers.map((inf) => (
                <Card key={inf.id} className="bg-card border-border/50" data-testid={`influencer-${inf.id}`}>
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold">{inf.name}</h3>
                        {inf.social_handle && (
                          <p className="text-sm text-muted-foreground">@{inf.social_handle}</p>
                        )}
                        <p className="text-sm text-primary mt-1">
                          Commission Rate: {(inf.commission_rate * 100).toFixed(0)}%
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-muted-foreground">Total Sales</p>
                        <p className="text-lg font-bold text-green-400">{formatCurrency(inf.total_sales)}</p>
                        <p className="text-sm text-muted-foreground mt-2">Commission Owed</p>
                        <p className="text-lg font-bold text-red-400">{formatCurrency(inf.total_owed)}</p>
                      </div>
                    </div>
                    <div className="flex gap-2 mt-4 pt-4 border-t border-border/50">
                      <Button size="sm" onClick={() => openAddSale(inf)} data-testid={`add-sale-${inf.id}`}>
                        <Plus className="h-4 w-4 mr-1" />
                        Record Sale
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => openEditInfluencer(inf)}>
                        <Edit2 className="h-4 w-4 mr-1" />
                        Edit
                      </Button>
                      <Button size="sm" variant="outline" className="text-destructive" onClick={() => handleDeleteInfluencer(inf.id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="sales" className="space-y-4 mt-6">
          {sales.length === 0 ? (
            <Card className="p-12 text-center bg-card border-border/50">
              <DollarSign className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No sales recorded yet</p>
            </Card>
          ) : (
            <Card className="bg-card border-border/50">
              <CardContent className="p-0">
                <div className="divide-y divide-border">
                  {sales.map((sale) => {
                    const inf = influencers.find(i => i.id === sale.influencer_id);
                    const event = events.find(e => e.id === sale.event_id);
                    return (
                      <div key={sale.id} className="p-4 flex justify-between items-center" data-testid={`sale-${sale.id}`}>
                        <div>
                          <p className="font-medium">{inf?.name || 'Unknown'}</p>
                          <p className="text-sm text-muted-foreground">
                            {sale.quantity} cards @ {formatCurrency(sale.sale_amount)}
                            {event && ` • ${event.name}`}
                          </p>
                          <p className="text-xs text-muted-foreground">{sale.date}</p>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className="text-sm text-muted-foreground">Commission</p>
                            <p className="font-bold text-primary">{formatCurrency(sale.commission_earned)}</p>
                          </div>
                          <Button size="sm" variant="ghost" className="text-destructive" onClick={() => handleDeleteSale(sale.id)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Add/Edit Influencer Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) { setEditingInfluencer(null); resetInfluencerForm(); } }}>
        <DialogContent className="bg-card">
          <DialogHeader>
            <DialogTitle className="text-2xl font-heading">
              {editingInfluencer ? 'Edit Influencer' : 'Add Influencer'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleInfluencerSubmit} className="space-y-4 pt-4">
            <div>
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={influencerForm.name}
                onChange={(e) => setInfluencerForm({ ...influencerForm, name: e.target.value })}
                required
                className="bg-secondary/50 border-transparent focus:border-primary"
              />
            </div>
            <div>
              <Label htmlFor="social_handle">Social Handle</Label>
              <Input
                id="social_handle"
                value={influencerForm.social_handle}
                onChange={(e) => setInfluencerForm({ ...influencerForm, social_handle: e.target.value })}
                placeholder="@username"
                className="bg-secondary/50 border-transparent focus:border-primary"
              />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={influencerForm.email}
                onChange={(e) => setInfluencerForm({ ...influencerForm, email: e.target.value })}
                className="bg-secondary/50 border-transparent focus:border-primary"
              />
            </div>
            <div>
              <Label htmlFor="commission_rate">Commission Rate (%)</Label>
              <Input
                id="commission_rate"
                type="number"
                min="0"
                max="100"
                value={influencerForm.commission_rate}
                onChange={(e) => setInfluencerForm({ ...influencerForm, commission_rate: e.target.value })}
                required
                className="bg-secondary/50 border-transparent focus:border-primary"
              />
            </div>
            <div>
              <Label htmlFor="notes">Notes</Label>
              <Input
                id="notes"
                value={influencerForm.notes}
                onChange={(e) => setInfluencerForm({ ...influencerForm, notes: e.target.value })}
                className="bg-secondary/50 border-transparent focus:border-primary"
              />
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit">{editingInfluencer ? 'Update' : 'Add'} Influencer</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Record Sale Dialog */}
      <Dialog open={saleDialogOpen} onOpenChange={(open) => { setSaleDialogOpen(open); if (!open) resetSaleForm(); }}>
        <DialogContent className="bg-card">
          <DialogHeader>
            <DialogTitle className="text-2xl font-heading">
              Record Sale {selectedInfluencer && `for ${selectedInfluencer.name}`}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaleSubmit} className="space-y-4 pt-4">
            <div>
              <Label htmlFor="quantity">Quantity (Cards Sold) *</Label>
              <Input
                id="quantity"
                type="number"
                min="1"
                value={saleForm.quantity}
                onChange={(e) => setSaleForm({ ...saleForm, quantity: e.target.value })}
                required
                className="bg-secondary/50 border-transparent focus:border-primary"
              />
            </div>
            <div>
              <Label htmlFor="sale_amount">Total Sale Amount ($) *</Label>
              <Input
                id="sale_amount"
                type="number"
                step="0.01"
                min="0"
                value={saleForm.sale_amount}
                onChange={(e) => setSaleForm({ ...saleForm, sale_amount: e.target.value })}
                required
                className="bg-secondary/50 border-transparent focus:border-primary"
              />
              {selectedInfluencer && saleForm.sale_amount && (
                <p className="text-sm text-primary mt-1">
                  Commission: {formatCurrency(parseFloat(saleForm.sale_amount) * selectedInfluencer.commission_rate)}
                </p>
              )}
            </div>
            <div>
              <Label htmlFor="event_id">Event (Optional)</Label>
              <select
                id="event_id"
                value={saleForm.event_id}
                onChange={(e) => setSaleForm({ ...saleForm, event_id: e.target.value })}
                className="w-full px-3 py-2 rounded-lg bg-secondary/50 border border-transparent focus:border-primary text-foreground focus:outline-none focus:ring-1 focus:ring-primary h-11"
              >
                <option value="">No specific event</option>
                {events.map((event) => (
                  <option key={event.id} value={event.id}>{event.name}</option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor="date">Date *</Label>
              <Input
                id="date"
                type="date"
                value={saleForm.date}
                onChange={(e) => setSaleForm({ ...saleForm, date: e.target.value })}
                required
                className="bg-secondary/50 border-transparent focus:border-primary"
              />
            </div>
            <div>
              <Label htmlFor="sale_notes">Notes</Label>
              <Input
                id="sale_notes"
                value={saleForm.notes}
                onChange={(e) => setSaleForm({ ...saleForm, notes: e.target.value })}
                className="bg-secondary/50 border-transparent focus:border-primary"
              />
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setSaleDialogOpen(false)}>Cancel</Button>
              <Button type="submit">Record Sale</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
